<!-- eslint-disable vue/multi-word-component-names -->
<script lang="ts">
    // @ts-nocheck
    import tabIcon from "@/assets/vardplatsoversikt.svg";

    export default {
        name: "Vardplatsbestallningsoversikt",
        tabName: "Beställningsöversikt",
        icon: tabIcon,
        refreshTab: true,
    };
</script>

<script setup lang="ts">
    import { ref, toRefs, computed, onBeforeMount, onBeforeUnmount, onMounted, onUnmounted } from "vue";
    import { getConfig, getErrorMessage, klinikenApi } from "@/api";
    import { openDialog } from "@/utils";
    import store from "@/store";
    import SearchFilter from "@/components/SearchFilter.vue";
    import KlinikenTable from "@/components/Table/KlinikenTable.vue";
    import ActionIconVardplatsbestallning from "@/assets/vardplatsbestallning.svg";
    import ActionIconStryk from "@/assets/stryk.svg";
    import StatusBestalldIcon from "@/assets/status_bestalld.svg";
    import StatusBokadIcon from "@/assets/statusbokad.svg";
    import StatusInskrivenIcon from "@/assets/status_inskriven.svg";
    import StatusUtskrivenIcon from "@/assets/status_utskriven.svg";
    import StatusStrukenIcon from "@/assets/status_struken.svg";
    import Vardplatsbestallning from "@/tabs/Vardplatsbokning/Vardplatsbestallning.vue";
    import { Vardplatsbestallning as VardplatsbestallningType, VardplatsStatus } from "@/tabs/Vardplatsbokning/types";
    import emitter from "tiny-emitter/instance";
    import ModalContent from "@/components/generic/ModalContent.vue";
    import StrykVardplatsbestallning from "@/tabs/Vardplatsbokning/StrykVardplatsbestallning.vue";

    const props = defineProps<{
        tabId: number;
    }>();

    const { tabId } = toRefs(props);

    defineExpose({
        tabId,
    });

    const listPage = ref<number>(1);
    const listCount = ref<number>(0);
    const listLimit = ref<number>(20);
    const filterParams = ref<{
        [key: string]: unknown;
        show_strukna?: boolean;
    }>({
        // BE returnerar strukna per default, om inte show_strukna: false är satt
        show_strukna: false,
    });
    const subsetName = ref<string>("");
    const vardplatsbestallningar = computed<VardplatsbestallningType[]>(() =>
        store.getters["vardplatsbestallning/vardplatsbestallningSubset"]({
            subsetName: subsetName.value,
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
            prependNewlyCreated: false,
        })
    );

    // Modaler

    const showStrykVardplatsbestallning = ref<boolean>(false);

    const strykVardplatsbestallning = (vardplatsbestallning: VardplatsbestallningType) => {
        selectedItem.value = vardplatsbestallning;
        showStrykVardplatsbestallning.value = true;
    };

    const selectedItem = ref<VardplatsbestallningType | undefined>();

    const columns = [
        {
            title: "PersonId",
            name: "patient.personId_formatted",
            sort: "patient__personId",
            lock: "left",
        },
        {
            title: "Patientnamn",
            name: "patient.namn_reverse",
            sort: "patient__namn",
        },
        {
            title: "Operationsdatum",
            name: "operationsdatum",
            sort: "operationsdatum",
            type: "date",
        },
        {
            title: "Operationstid",
            name: "operationstid",
            type: "time",
        },
        {
            title: "Inställelsedatum",
            name: "installelsedatum",
            sort: "installelsedatum",
            type: "date",
        },
        {
            title: "Inställelsetid",
            name: "installelsetid",
            type: "time",
        },
        {
            title: "Antal vårddygn",
            name: "antal_varddygn",
        },
        {
            title: "Operatör",
            name: "operator.namn",
            sort: "operator",
        },
        {
            title: "Vårdgivare",
            name: "vardgivare.alias",
            sort: "vardgivare__alias",
        },
        {
            title: "Huvuddiagnos",
            name: "huvuddiagnos.code",
            sort: "huvuddiagnos__code",
        },
        {
            title: "Status",
            name: "slutenvardsbesok.status",
            icons: {
                Beställd: StatusBestalldIcon,
                Bokad: StatusBokadIcon,
                Inskriven: StatusInskrivenIcon,
                Utskriven: StatusUtskrivenIcon,
                Struken: StatusStrukenIcon,
            },
        },
    ];

    const dropdownActions = computed(() => {
        return [
            {
                title: "Vårdplatsbeställning",
                name: "openVardplatsbestallning",
                icon: ActionIconVardplatsbestallning,
                show: () => true,
            },
            {
                title: "Stryk beställning",
                name: "strykVardplatsbestallning",
                show: (item: any) => [VardplatsStatus.BESTALLD].includes(item.slutenvardsbesok.status),
                icon: ActionIconStryk,
            },
        ];
    });

    const sort = async (sort: string) => {
        filterParams.value = {
            ...(filterParams.value ? filterParams.value : {}),
            ordering: sort,
        };

        await fetchVardplatsbestallningar({
            page: 1,
        });
    };

    const loadPage = async (page: number) => {
        await fetchVardplatsbestallningar({ page });
    };

    const changeLimit = async (value: number) => {
        listLimit.value = value;

        await fetchVardplatsbestallningar({
            limit: value,
            page: 1,
        });
    };

    // Sökfiltrering

    const filterValues = ref<any>({});

    const filterFields = computed(() => ({
        q: { label: "Sök" },
        patient__namn: { label: "Patientnamn" },
        operationsdatum__gte: { label: "Operationsdatum från" },
        operationsdatum__lte: { label: "Operationsdatum till" },
        vardgivare: { label: "Vårdgivare", options: vardgivare.value },
        operator: { label: "Operatör", options: vardpersoner.value },
    }));

    const handleSearch = async (data: { [key: string]: unknown }) => {
        const filterData = Object.entries(data).filter(([name, value]) => value !== "");

        filterParams.value = Object.fromEntries(filterData);
        filterValues.value = filterParams.value;

        await fetchVardplatsbestallningar({ page: 1 });
    };

    const vardgivare = ref([]);
    const loadAndMapVardgivare = async () => {
        try {
            const response = await klinikenApi.get("/core/externavardgivare/", getConfig());
            vardgivare.value = response.data.results.map((vg: any) => ({
                value: vg.id,
                label: vg.alias || vg.namn,
            }));
        } catch (error) {
            openDialog("Kunde inte hämta vårdgivare.", "warning");
        }
    };

    const vardpersoner = ref([]);
    const loadAndMapVardpersonerFromVardgivare = async ({ value }: { value: unknown }) => {
        if (value) {
            try {
                const response = await klinikenApi.get(
                    "/core/externavardpersoner/",
                    getConfig({
                        params: {
                            vardgivare: value,
                        },
                    })
                );
                vardpersoner.value = response.data.results.map((item: any) => {
                    return {
                        value: item.id,
                        label: `${item.fornamn} ${item.efternamn}`,
                    };
                });
            } catch (e) {
                openDialog(getErrorMessage(e), "error");
            }
        } else {
            vardpersoner.value = [];
        }
    };

    const fetchVardplatsbestallningar = async ({
        forceRefresh,
        page,
        resetSubset,
        resetNewlyCreated,
        limit = listLimit.value,
    }: {
        page: number;
        limit?: number;
        forceRefresh?: boolean;
        resetSubset?: boolean;
        resetNewlyCreated?: boolean;
    }) => {
        try {
            /*
Sätter om page innan resten, för att tillåta rendering av existerande data i store.
Görs inte om subset ska resettas. Då vill vi inte visa tidigare data
*/
            if (!resetSubset) {
                listPage.value = page;
            }

            // Visar bara laddningsikon om det inte finns någon existerande data i store
            if (!vardplatsbestallningar.value.length) {
                store.dispatch("tabs/displaySpinner", { id: tabId.value, display: true });
            }

            const {
                nextPage,
                total,
                subsetName: fetchedSubsetName,
            } = await store.dispatch("vardplatsbestallning/fetchVardplatsbestallningar", {
                page,
                limit,
                resetNewlyCreated,
                /*
TODO Ska inte vara hårdkodat till true när vi har en lösning för att invalidera dat i store och
göra nya automatiska hämtningar
*/
                forceRefresh: true,
                filterParams: filterParams.value,
            });

            /*
             * Sätter om page efter hämtning i de fall där subset ska resettas och inte kommer visa tidigare data
             */
            if (resetSubset) {
                listPage.value = page;
            }

            subsetName.value = fetchedSubsetName;

            if (typeof total === "number") {
                listCount.value = total;
            }
        } catch (error) {
            console.error(error);

            openDialog("Kunde inte hämta vårdplatsbeställningar.", "warning");
        } finally {
            store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });
        }
    };

    const setActivePatient = async (vardplatsbestallning: VardplatsbestallningType) => {
        if (vardplatsbestallning.patient) {
            await store.dispatch("patientData/loadFromBackend", {
                id: vardplatsbestallning.patient.id,
                failSilently: false,
            });
            selectedItem.value = vardplatsbestallning;
        }
    };

    const openVardplatsbestallning = async (vardplatsbestallning: VardplatsbestallningType) => {
        await store.dispatch("tabs/closeTabWithUnsavedDataCheck", Vardplatsbestallning, { root: true });
        store.dispatch("tabs/openTab", {
            component: Vardplatsbestallning,
            parameters: { id: vardplatsbestallning.id },
        });
    };

    onMounted(async () => {
        emitter.on(`refreshTab-${tabId?.value}`, async () => {
            await fetchVardplatsbestallningar({
                page: listPage.value,
                limit: listLimit.value,
                forceRefresh: true,
                resetSubset: true,
                resetNewlyCreated: true,
            });
        });
    });

    onUnmounted(() => {
        emitter.off(`refreshTab-${tabId?.value}`);
    });

    onBeforeMount(async () => {
        store.commit("vardplatsbestallning/addSubscriber");
        await fetchVardplatsbestallningar({ page: listPage.value });
        await loadAndMapVardgivare();
    });

    onBeforeUnmount(() => {
        store.commit("vardplatsbestallning/removeSubscriber");
    });
</script>

<template>
    <div class="vardplatsbestallningsoversikt">
        <div class="header">
            <h2>Vårdplatsbeställningar</h2>
        </div>

        <SearchFilter id="search-filter" v-model="filterValues" :fields="filterFields" @input="handleSearch">
            <template v-slot:header-filters>
                <KText class="search-field" name="q" placeholder="Sök" />
            </template>
            <template v-slot:fields>
                <KText name="patient__namn" label="Patientnamn" />
                <FormulateInput type="datepicker" name="operationsdatum__gte" label="Operationsdatum från" />
                <FormulateInput type="datepicker" name="operationsdatum__lte" label="Operationsdatum till" />
                <FormulateInput
                    type="vueselect"
                    name="vardgivare"
                    label="Vårdgivare"
                    :options="vardgivare"
                    @option:selected="loadAndMapVardpersonerFromVardgivare"
                />
                <FormulateInput type="vueselect" name="operator" label="Operatör" :options="vardpersoner" />
            </template>
        </SearchFilter>

        <div class="subheader">
            <span>
                Visar <strong>{{ vardplatsbestallningar.length }}</strong> av <strong>{{ listCount }}</strong> träffar
            </span>
        </div>

        <KlinikenTable
            :columns="columns"
            :items="vardplatsbestallningar"
            :selectedItem="selectedItem"
            paginationStyle="paging"
            :count="listCount"
            :limit="listLimit"
            :currentPage="listPage"
            :lockColumns="true"
            :hasDropdown="true"
            :dropdown-actions="dropdownActions"
            @sort="sort"
            @loadPage="loadPage"
            @changeLimit="changeLimit"
            @tableRowClicked="setActivePatient"
            @openVardplatsbestallning="openVardplatsbestallning"
            @strykVardplatsbestallning="strykVardplatsbestallning"
        />
        <ModalContent
            title="Stryk vårdplatsbeställning"
            v-if="showStrykVardplatsbestallning && selectedItem"
            @close="showStrykVardplatsbestallning = false"
            showCloseButton
        >
            <StrykVardplatsbestallning
                :vardplatsbestallningId="selectedItem.id"
                @close="showStrykVardplatsbestallning = false"
            />
        </ModalContent>
    </div>
</template>

<style lang="scss">
    .vardplatsbestallningsoversikt {
        .header {
            display: flex;
            gap: 20px;
        }
    }
</style>
